import { isResource } from '@medplum/core'
import type { Basic, CodeableConcept, Coding } from '@medplum/fhirtypes'
import type { LabOrganization, TestCoding } from '@medplum/health-gorilla-core'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import { BASIC_CODE_SYSTEM, SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { today } from '#src/dates.ts'
import { Extension } from '#src/decorators.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedBasic } from '#src/models/basic.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class LabOrderSet extends ExtendedBasic {
  static isInstance(value: unknown): value is Basic {
    return (
      isResource(value) &&
      isOfType(value, 'Basic') &&
      hasCodeableConcept(value.code, codeableConcept(CODE))
    )
  }

  constructor({
    name,
    vendor,
    tests,
    diagnoses,
  }: {
    name: string
    vendor: LabOrganization
    tests: TestCoding[]
    diagnoses: DiagnosisCodeableConcept[]
  }) {
    super({
      code: codeableConcept(CODE),
      created: today(),
      identifier: [
        {
          system: LAB_ORDER_SET_IDENTIFIER_SYSTEM,
          value: name,
        },
      ],
    })
    this.vendor = vendor
    this.tests = tests
    this.diagnoses = diagnoses
  }

  @Extension({
    url: `${SEEN_HEALTH_SYSTEM}/StructureDefinition/lab-order-set-vendor`,
    kind: 'valueString',
  })
  _vendor?: string

  get vendor(): LabOrganization {
    const raw = this._vendor
    invariant(raw, 'Missing vendor')
    return JSON.parse(raw)
  }
  set vendor(v: LabOrganization) {
    this._vendor = JSON.stringify(v)
  }

  @Extension({
    url: `${SEEN_HEALTH_SYSTEM}/StructureDefinition/lab-order-set-tests`,
    kind: 'valueString',
  })
  _tests?: string

  get tests(): TestCoding[] {
    const raw = this._tests
    invariant(raw, 'Missing tests')
    return JSON.parse(raw)
  }
  set tests(tests: TestCoding[]) {
    this._tests = JSON.stringify(tests)
  }

  @Extension({
    url: `${SEEN_HEALTH_SYSTEM}/StructureDefinition/lab-order-set-diagnoses`,
    kind: 'valueString',
  })
  _diagnoses?: string

  get diagnoses(): DiagnosisCodeableConcept[] {
    const raw = this._diagnoses
    invariant(raw, 'Missing diagnoses')
    return JSON.parse(raw)
  }
  set diagnoses(diagnoses: DiagnosisCodeableConcept[]) {
    this._diagnoses = JSON.stringify(diagnoses)
  }

  get name(): string {
    const name = this.resource.identifier?.[0]?.value
    invariant(name, 'Missing name')
    return name
  }

  set name(value: string) {
    this.resource.identifier = [{ value }]
  }

  get searchKeywords(): string[] {
    return [
      this.name,
      this.vendor?.name ?? '',
      ...(this.tests?.map((t) => t.display ?? t.code) ?? []),
      ...(this.diagnoses?.map((dx) => dx.coding?.[0]?.code ?? '') ?? []),
    ]
  }
  static byName(name: string): QueryTypes {
    return {
      code: CODE,
      identifier: { system: LAB_ORDER_SET_IDENTIFIER_SYSTEM, value: name },
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      code: CODE,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }
}

const CODE = {
  system: BASIC_CODE_SYSTEM,
  code: 'lab-order-set',
  display: 'Lab Order Set',
}

export const LAB_ORDER_SET_IDENTIFIER_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Basic/lab-order-set-identifier`

type DiagnosisCodeableConcept = CodeableConcept & {
  coding: (Coding & Required<Pick<Coding, 'system' | 'code'>>)[]
}
